import React, { useContext, useEffect, useState } from 'react';
import { Box, TextField } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import PropTypes from 'prop-types';

import Button from '~/components/Button';
import QuickBookRowAutocomplete from '~/components/QuickBookRow/QuickBookRowAutocomplete';
import QuickBookRowNotesAndAttachment from '~/components/QuickBookRow/QuickBookRowNotesAndAttachment';
import QuickBookRowScopeSelect from '~/components/QuickBookRow/QuickBookRowScopeSelect';
import {
  setInitialJournalScopeBasedOnAccount,
  setUpJournalForSubmit,
} from '~/components/QuickBookRow/transactionTemplates/helpers/journalFunctions';
import { useStyles } from '~/components/QuickBookRow/transactionTemplates/styles';
import { getManualBasicJournal } from '~/components/SearchSelect/TransactionTypeOptions';
import { PersonContext } from '~/contexts/PersonContext';
import { create } from '~/feathersFunctionalWrapper';
import { checkIsSmallScreen } from '~/functions/ScreenSizeFunctions';
import { useSegmentTrack } from '~/functions/SegmentFunctions';
import { getAccountOptions } from '~/helpers/utils/functionalAutocompleteLibrary';

import QuickBookRowLoading from '../QuickBookRowLoading';

export default function QuickBookRowRefundIssuedTemplate({ transaction, onTransactionAction, onGetHelp }) {
  const isMobile = checkIsSmallScreen();
  const tracking = useSegmentTrack();
  const { organizationId } = useContext(PersonContext);
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [revenueAccountOptions, setRevenueAccountOptions] = useState([]);
  const [journal, setJournal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const getData = async () => {
      const revenueAccountOptions = await getAccountOptions(organizationId, {
        $or: [{ type: 'Revenue' }, { type2: 'Deferred Revenue' }],
      });
      setRevenueAccountOptions(revenueAccountOptions);

      const journalObject = getManualBasicJournal();
      const { account, id, description, date, amount } = transaction;
      journalObject.creditLines[0].accountIdSelect = account;
      journalObject.creditLines[0].transactionId = id;
      journalObject.description = description;
      journalObject.date = date;
      journalObject.amount = amount;
      await setInitialJournalScopeBasedOnAccount(transaction.account, journalObject);
      setJournal(journalObject);
      setIsLoading(false);
    };
    getData();
  }, [isLoading, organizationId, transaction]);

  const addRefund = async () => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const journalData = {
      journal,
      organizationId,
      direction: 'cash out',
      transactionId: transaction.id,
      type: 'Refund Issued',
      bookAsRefund: false,
    };

    const journalSubmit = setUpJournalForSubmit(journalData);

    try {
      await create('journals', journalSubmit);
      setIsSubmitting(false);
      onTransactionAction();
    } catch (error) {
      setError(error);
      setIsSubmitting(false);
      return;
    }
  };

  if (isLoading) {
    return <QuickBookRowLoading />;
  }

  return (
    <form onSubmit={addRefund}>
      <Box className={classes.rootContainer}>
        <Box className={classes.container}>
          <QuickBookRowAutocomplete
            className={classes.autoCompleteField}
            displaySubaccounts
            options={revenueAccountOptions}
            onChange={(value) => {
              setJournal({ ...journal, debitLines: [{ ...journal.debitLines[0], accountIdSelect: value }] });
            }}
            label="Revenue Account"
            value={journal.debitLines[0].accountIdSelect}
            required
            getOptionSelected={(option, value) => option.id === value.id}
          />
          <TextField
            className={isMobile ? classes.mobileDescriptionField : classes.descriptionFieldSixtySixPercent}
            label="Description"
            placeholder="Description"
            variant="outlined"
            size="small"
            value={journal.description}
            onChange={(event) => {
              setJournal({ ...journal, description: event.target.value });
            }}
          />
        </Box>
        <QuickBookRowScopeSelect journal={journal} setJournal={setJournal} />
        <QuickBookRowNotesAndAttachment
          journal={journal}
          onChange={(newJournal) => {
            setJournal(newJournal);
          }}
        />
        {error && error.message ? (
          <Alert severity="error" className={classes.errorAlert}>
            {error.message}
          </Alert>
        ) : null}
        <Box className={classes.actionsContainer}>
          <Button
            variant="outlined"
            size="small"
            className={classes.helpButton}
            onClick={() => {
              tracking('get_help clicked', { location: 'Inline Booking - Import Feed' });
              onGetHelp();
            }}
          >
            <HelpOutline className={classes.helpIcon} />
            Get Help
          </Button>
          <Box className={classes.rowActionsSecondaryContainer}>
            <Button
              variant="contained"
              className={classes.saveButton}
              size="small"
              fullWidth={false}
              type="submit"
              onClick={() => {
                tracking('save clicked', { location: 'Inline Booking - Import Feed' });
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

QuickBookRowRefundIssuedTemplate.propTypes = {
  transaction: PropTypes.object.isRequired,
  onTransactionAction: PropTypes.func.isRequired,
  onGetHelp: PropTypes.func.isRequired,
};
